import React from "react";

export enum PricingModel {
  PER_UNIT = 1,
  FLEXIBLE = 2,
  TIERED = 3,
  USAGE_BASED = 4,
  TIME_BASED = 5,
  FLAT_RATE = 6
}

export enum ChargeType {
  RECURRING = 1,
  ONE_TIME = 2
}

export enum BillingFrequency {
  MONTHLY = 1,
  QUARTERLY = 2,
  ANNUALLY = 3,
  ONCE = 4,
  CUSTOM = 5
}

export enum ServiceDuration {
  ONE_MONTH = 1,
  ONE_YEAR = 2,
  TWO_YEARS = 3,
  THREE_YEARS = 4,
  CUSTOM = 5
}

export interface Pricing {
  id: string;
  billingFrequency: number | null;
  price: number | null;
  convertedPrice: number | null;
  serviceItemPeriods: number | null;
}

export interface Section {
  id: number;
  name: string;
}

export interface ServiceItemType {
  id: number;
  sectionId: number;
  name: string;
  defaultChargeType: ChargeType;
  defaultPricingModels: PricingModel;
}

export interface PricingModelOption {
  id: PricingModel;
  name: string;
  description: string;
  isEnabled: boolean;
}

export interface ServiceItem {
  id: string;
  productId: string;
  typeId: number;
  name: string;
  description: string | null;
  pricingModel: number;
  chargeType: ChargeType;
  pricing: Pricing[];
  itemOrder: number;
  // Present (always false) on bulk-catalog items too — the bulk endpoint
  // reuses the same backend schema as the soft-deleted-item resolution
  // fallback (useResolveServiceItemById), which is the only place a `true`
  // value can come from since the bulk query filters deleted items out.
  isDeleted?: boolean;
  // Whether the service is in any bundle; null when the backend could not tell.
  inBundles?: boolean | null;
}

// A service item as the by-id lookup returns it. That lookup isn't scoped to
// a product, so the item's product is unknown.
export type ServiceItemDetail = Omit<ServiceItem, "productId">;

export type ServiceItemsOrderPayload = {
  items: Record<number, string>;
};

export interface ProductV2 {
  id: string;
  name: string;
  description: string | null;
  serviceItems: ServiceItem[];
}

// A service in a bundle, with the one pricing term the bundle uses.
export interface ProductBundleItem {
  serviceItemId: string;
  serviceItemName: string | null;
  serviceItemType: number | null;
  pricingModel: number | null;
  chargeType: ChargeType | null;
  serviceItemPricingId: string;
  serviceItemPeriods: number | null;
  billingFrequency: number | null;
  price: number | null;
  quantity: number;
  itemOrder: number | null;
  total: number;
}

export interface ProductBundle {
  id: string;
  productId: string;
  name: string;
  description: string | null;
  itemOrder: number | null;
  items: ProductBundleItem[];
  // Computed by the backend on every read.
  total: number;
  // The bundle's default discount: true for a percentage, false for an amount,
  // null with none. Optional, as other bundle consumers don't read it.
  isDiscountPercentage?: boolean | null;
  discountAmount?: number | null;
  // The total less the discount, never below zero.
  discountedTotal?: number;
}

export interface ProductBundlesList {
  productId: string;
  bundles: ProductBundle[];
}

export interface ProductBundleItemInput {
  serviceItemId: string;
  serviceItemPricingId: string;
  quantity: number;
}

// Create and update both send the whole bundle; items are in display order.
export interface ProductBundleInput {
  name: string;
  description: string | null;
  items: ProductBundleItemInput[];
  isDiscountPercentage: boolean | null;
  // Null removes the discount; leaving both fields out keeps the stored one.
  discountAmount: number | null;
}

export interface ProductFormData {
  name: string;
  description: string;
}

export interface ServiceItemFormData {
  name: string;
  description: string;
  typeId: number;
  pricingModel: PricingModel;
  chargeType: ChargeType;
  pricing?: PricingTableRow[];
  updateOnly?: boolean;
}

export interface PricingTableRow {
  id: string;
  serviceItemPeriods: ServiceDuration | null;
  billingFrequency: BillingFrequency | null;
  price: number;
  convertedPrice?: number | null;
}

export interface PricingTableProps {
  pricing: PricingTableRow[];
  onPricingChange: (pricing: PricingTableRow[]) => void;
  chargeType: ChargeType;
}

export interface UseServiceItemFormProps {
  serviceItem?: ServiceItem | null;
  productId: string;
  isEditMode?: boolean;
}

export interface ServiceItemFormState {
  formData: Partial<ServiceItemFormData>;
  pricing: PricingTableRow[];
  chargeType: ChargeType;
  oneTimePrice: number;
}

export interface FormValidData {
  name?: string;
  description?: string;
  typeId?: number;
  pricingModel?: PricingModel;
  chargeType?: ChargeType | string;
  oneTimePrice?: number;
}

export interface FormSetValueInterface {
  setValue: (
    field: string,
    value: string | number | PricingModel | ChargeType
  ) => void;
}

export interface ServiceItemApiPayload {
  name: string;
  description: string;
  typeId: number;
  pricingModel: number;
  chargeType: number;
  pricing: Pricing[];
  updateOnly?: boolean;
}

export interface UseServiceItemFormReturn {
  formState: ServiceItemFormState;
  isFormValid: boolean;
  formRef: React.RefObject<HTMLFormElement>;
  formSetValue: FormSetValueInterface | null;
  setFormSetValue: (setValue: FormSetValueInterface | null) => void;
  handleFormValid: (valid: boolean, data: FormValidData | null) => void;
  handleFormSubmit: () => Promise<ServiceItem>;
  handleSubmitButton: () => void;
  isSubmitting: boolean;
  setPricing: (pricing: PricingTableRow[]) => void;
  setChargeType: (chargeType: ChargeType) => void;
  setOneTimePrice: (price: number) => void;
  isUpdateOnly: () => boolean;
}
